import { createContext, useContext } from 'react';
import type { UseFocusableResult } from '@noriginmedia/norigin-spatial-navigation';
import {
  init,
  FocusContext as NoriginFocusContext,
  setKeyMap,
  useFocusable as useNoriginFocusable,
} from '@noriginmedia/norigin-spatial-navigation';

import type {
  UseFocusableConfig,
  UseFocusableReturnType,
} from '@/shared/lib/norigin-spatial-navigation/focusable.types';

import {
  KEY_CODE_KEYBOARD_DOWN,
  KEY_CODE_KEYBOARD_ENTER,
  KEY_CODE_KEYBOARD_LEFT,
  KEY_CODE_KEYBOARD_RIGHT,
  KEY_CODE_KEYBOARD_UP,
} from '../../constants/key-code.constants';

// ----------------------------------------------------------------------

export type UseFocusableContextConfig<ExtraProps extends object = {}> = Omit<
  UseFocusableConfig<ExtraProps>,
  'shouldFocusOnClick' | 'onArrowPress' | 'onPress' | 'onEntering' | 'onFocus' | 'onBlur'
>;

type ProviderProps = Pick<UseFocusableReturnType, 'focusKey'> & Pick<UseFocusableContextConfig, 'isFocusable'>;

export type FocusProviderProps = React.PropsWithChildren<ProviderProps>;

type NavigationContextProps = Pick<ProviderProps, 'isFocusable'>;

export type UseFocusableContextReturnType = Omit<UseFocusableResult, 'focused' | 'focusKey'> & {
  providerProps: ProviderProps;
};

// ----------------------------------------------------------------------

const isDebugMode = false;

/**
 * {@link https://github.com/NoriginMedia/Norigin-Spatial-Navigation#init}
 */
init({
  debug: isDebugMode && import.meta.env.DEV,
  visualDebug: isDebugMode && import.meta.env.DEV,
  // throttleKeypresses: false,
  // throttle: 100,
});

/**
 * {@link https://github.com/NoriginMedia/Norigin-Spatial-Navigation#setkeymap}
 */
setKeyMap({
  up: KEY_CODE_KEYBOARD_UP,
  right: KEY_CODE_KEYBOARD_RIGHT,
  down: KEY_CODE_KEYBOARD_DOWN,
  left: KEY_CODE_KEYBOARD_LEFT,
  enter: KEY_CODE_KEYBOARD_ENTER,
});

// ----------------------------------------------------------------------

export const FocusContext = createContext<NavigationContextProps | null>(null);

if (import.meta.env.DEV) {
  FocusContext.displayName = 'FocusContext';
}

// ----------------------------------------------------------------------

export const useFocusContext = () => {
  const context = useContext(FocusContext);

  if (!context) {
    throw new Error('useFocusContext must be used within a NavigationContext');
  }

  return context;
};

// ----------------------------------------------------------------------

/**
 * {@link https://github.com/NoriginMedia/Norigin-Spatial-Navigation#focuscontext-required-for-focusable-containers}
 */
export const FocusProvider = (props: FocusProviderProps) => {
  const { focusKey, children } = props;

  return (
    <NoriginFocusContext.Provider value={focusKey}>
      <FocusContext.Provider value={props}>{children}</FocusContext.Provider>
    </NoriginFocusContext.Provider>
  );
};

// ----------------------------------------------------------------------

export const useFocusableContext = <ExtraProps extends object>(
  config: UseFocusableContextConfig<ExtraProps> = {},
): UseFocusableContextReturnType => {
  const { isFocusable, shouldSaveLastFocusedChild, shouldTrackChildren, shouldAutoRestoreFocus, ...restOfConfig } =
    config;

  const { focusKey, ...restOfFocusableResult } = useNoriginFocusable({
    focusable: isFocusable,
    saveLastFocusedChild: shouldSaveLastFocusedChild,
    trackChildren: shouldTrackChildren,
    autoRestoreFocus: shouldAutoRestoreFocus,
    ...restOfConfig,
  });

  const providerProps = {
    isFocusable,
    focusKey,
  };

  return {
    providerProps,
    ...restOfFocusableResult,
  };
};
